import { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import { FlexContainer, Text, Buttons, Spinner, ModalContext } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector, searchSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import { useFetchPreviousYear } from 'Containers/Dashboard/hooks';
import { calculateCompensationInfo } from 'Containers/Filing/components/ClabeForm/utils';
import { ReviewModal } from 'Components/Modal/components';
import SaveCardModal from 'Components/Modal/components/SaveCardModal';
import { routes } from 'Constants/routes';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import { FRONT_PLANS, PLAN_FLEX_LITE, plansNameMapping } from 'Constants/plans';
import { ResultTypes } from 'Types/common';
import * as Styled from './styles';

const FLEX_LITE_CONTEXT = 'flexLite';
const FED_USER_CONTEXT = 'fedUser';

const RevisionDeclaration = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.FILING);
  const { showModal } = useContext(ModalContext);
  const { isLoading, data } = useFetchPreviousYear();
  const info = useSelector(routeStateSelector);
  const planes = useSelector(searchSelector);
  const infoPlans = queryString.parse(planes);
  const customerInfo = useGetUserCustomerData();

  const plan = useMemo(
    () =>
      customerInfo && customerInfo.activePlan
        ? customerInfo.activePlan.name === PLAN_FLEX_LITE
          ? FRONT_PLANS.FLEX_LITE
          : plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );

  const havePreviousYears = data?.refundStatusType === ResultTypes.toReturn;
  const isExtendedFlexLite = info?.isExtendedFlexLite;

  const amountTotal = Math.abs(Number(data?.amountPending));
  const amountResult = Math.abs(Number(info?.taxReturnResult));

  const maiorDA = amountResult > amountTotal;

  const checkoutParams = {
    filing: 'true',
    plans: FRONT_PLANS.FLEX,
    fromFedUser: 'false',
    submitFlex: 'false',
    saveCard: 'true',
    submitFlexLite:
      plan === FRONT_PLANS.FLEX_LITE || infoPlans.plans === FRONT_PLANS.FLEX_LITE
        ? 'true'
        : 'false',
  };
  const isFlexUser = plan === FRONT_PLANS.FLEX_LITE || infoPlans.plans === FRONT_PLANS.FLEX_LITE;
  useEffect(() => {
    if (isFlexUser && !info?.alreadySavedCard) {
      showModal({
        comp: (
          <SaveCardModal
            successUrl={routes.DASHBOARD}
            cancelUrl={routes.DASHBOARD}
            checkoutParams={checkoutParams}
          />
        ),
      });
    }
  }, [isFlexUser]);

  const handleClick = (): void => {
    if (maiorDA && info?.compensation) {
      showModal({
        comp: <ReviewModal />,
      });
    } else if (info?.compensation) {
      history.push(routes.PREVIOUS_YEARS_FILTER);
    } else if (havePreviousYears) {
      history.push(routes.FILING_UPSELL);
    } else {
      history.push(routes.DASHBOARD);
    }
  };

  const infoContext = [
    `ReviewDeclaration.steps.one`,
    `ReviewDeclaration.steps.two`,
    `ReviewDeclaration.steps.three`,
  ];

  const imgUrl = isExtendedFlexLite
    ? `${TAXDOWN_ASSETS_DOMAIN}/illustrations/default/done_s.png`
    : `${TAXDOWN_ASSETS_DOMAIN}/illustrations/states/doc_correct_s.png`;

  const { taxReturnType } = calculateCompensationInfo(data, info);

  return (
    <Styled.Container direction="column" justify="space-between" gap="dimension_24" fullHeight>
      {isLoading ? (
        <FlexContainer justify="center" align="center">
          <Spinner size="M" align="center" mode="primary" />
        </FlexContainer>
      ) : (
        <>
          <FlexContainer
            direction="column"
            align="center"
            justify="center"
            gap="dimension_24"
            fullWidth
          >
            <img src={imgUrl} alt="finish_img" />
            <Text type="headline" size="XS_L_S" color="dark" align="center">
              {t(`ReviewDeclaration.title`, {
                context:
                  (info?.fromFedUser && FED_USER_CONTEXT) ||
                  (isExtendedFlexLite && FLEX_LITE_CONTEXT),
              })}
            </Text>
            <Styled.NextStepsContainer direction="column" justify="center" gap="dimension_16">
              <FlexContainer direction="column" gap="dimension_08">
                <Text type="paragraphs" size="M_XL_M" color="dark" align="center">
                  {t(`ReviewDeclaration.steps.title`, {
                    context:
                      (info?.fromFedUser && FED_USER_CONTEXT) ||
                      (isExtendedFlexLite && FLEX_LITE_CONTEXT),
                  })}
                </Text>
                <Text type="paragraphs" size="XS_S_M" color="dark" align="center">
                  {t(`ReviewDeclaration.steps.subtitle`, {
                    context:
                      (info?.fromFedUser && FED_USER_CONTEXT) ||
                      (isExtendedFlexLite && FLEX_LITE_CONTEXT),
                  })}
                </Text>
              </FlexContainer>
              {infoContext.map((text, i) => (
                <FlexContainer key={text} gap="dimension_16" direction="column">
                  <FlexContainer gap="dimension_08">
                    <Styled.BulletIcon justify="center" align="center">
                      <Text type="paragraphs" size="S_L_S">
                        {i + 1}
                      </Text>
                    </Styled.BulletIcon>
                    <Text size="S_L_M" type="paragraphs" color="dark">
                      {t(text, {
                        context:
                          info?.fromFedUser && FED_USER_CONTEXT
                            ? FED_USER_CONTEXT
                            : isExtendedFlexLite
                              ? FLEX_LITE_CONTEXT
                              : `${taxReturnType}`,
                      })}
                    </Text>
                  </FlexContainer>
                </FlexContainer>
              ))}
            </Styled.NextStepsContainer>
            <Text type="paragraphs" size="M_L_M" color="fresh" align="center">
              {t(`ReviewDeclaration.steps.continue.info`, {
                context: havePreviousYears && !maiorDA && 'previousYears',
              })}
            </Text>
          </FlexContainer>
          <FlexContainer direction="column" align="center" justify="center">
            <Buttons.Classic
              mode="primary"
              size="m"
              label={t('RevisionDeclaration.accept.button')}
              onClick={handleClick}
            />
          </FlexContainer>
        </>
      )}
    </Styled.Container>
  );
};

export default RevisionDeclaration;
